import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import intl from "react-intl-universal";
import VerificationInput from "react-verification-input";
import { useHistory } from "react-router-dom";
import { FiAlertTriangle, FiInfo, FiSmartphone, FiXCircle } from "react-icons/fi";
import { Countdown } from "../../../Components/Molecules";
import { FidoTouchPanel } from "../../../Components/Molecules/FidoTouchPanel";
import { TwoFactorMethodPicker } from "../../../Components/Molecules/TwoFactorMethodPicker";
import { useAuth } from "../../../Contexts/AuthContext";
import {
  normalizeApiTwoFactorMethods,
  normalizeLoginDocument,
  readTwoFactorRecord,
  resolveDefaultVerificationMethod,
  resolveTwoFactorAccountKey,
  resolveVerificationPageMethods,
  type VerificationLoginMethod,
} from "../../../Constants/webauthnMock";
import {
  authenticateSecurityKey,
  WebAuthnError,
} from "../../../Services/webauthnClient";

const OTP_METHODS = new Set<VerificationLoginMethod>([
  "email",
  "sms",
  "whatsapp",
]);

const CODE_METHODS = new Set<VerificationLoginMethod>([
  "email",
  "sms",
  "whatsapp",
  "google_authenticator",
]);

const isOtpMethod = (
  method: VerificationLoginMethod | null
): method is VerificationLoginMethod =>
  method !== null && OTP_METHODS.has(method);

const isCodeMethod = (
  method: VerificationLoginMethod | null
): method is VerificationLoginMethod =>
  method !== null && CODE_METHODS.has(method);

const normalizeTwoFactorMethods = (
  methods: string[] | undefined | null
): VerificationLoginMethod[] => normalizeApiTwoFactorMethods(methods);

const VerificationCodeLogin: React.FC = () => {
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const [code, setCode] = useState("");
  const [verificationDisable, setVerificationDisable] = useState(false);
  const [selectedMethod, setSelectedMethod] =
    useState<VerificationLoginMethod | null>(null);
  const [otpReady, setOtpReady] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [showMethodPicker, setShowMethodPicker] = useState(false);
  const otpInputRef = useRef<HTMLInputElement>(null);

  const {
    twoFactor,
    twoFactorFidoMock,
    completeWebAuthnLogin,
    getPilotTwoFactorPreferences,
    isLoading,
    sendVerificationCode,
    resendCode,
    user,
    otpDeliveryNotice,
    clearOtpDeliveryNotice,
    clearOtpExpiredNotice,
    showOtpExpiredNotice,
  } = useAuth();

  const pilotPrefs = useMemo(
    () => getPilotTwoFactorPreferences(),
    [getPilotTwoFactorPreferences, user.username]
  );

  const accountKey = useMemo(
    () => resolveTwoFactorAccountKey({ username: user.username }),
    [user.username]
  );

  const twoFactorRecord = useMemo(
    () => readTwoFactorRecord(accountKey),
    [accountKey]
  );

  const apiMethods = useMemo(
    () => normalizeTwoFactorMethods(user.two_factor_methods),
    [user.two_factor_methods]
  );

  const enabledMethods = useMemo(
    () =>
      resolveVerificationPageMethods(apiMethods, twoFactorRecord, {
        hasFidoKey: pilotPrefs.hasFidoKey,
        hasWebAuthn: user.has_web_authn === true,
        isPilot: pilotPrefs.isPilot,
      }),
    [
      apiMethods,
      pilotPrefs.hasFidoKey,
      pilotPrefs.isPilot,
      twoFactorRecord,
      user.has_web_authn,
    ]
  );

  useEffect(() => {
    if (selectedMethod || enabledMethods.length === 0) return;

    // Prefer the channel BaaS already dispatched (otp_sent_via). Do not re-challenge
    // another OTP channel on mount — that previously caused SMS+WhatsApp dual-send.
    const defaultMethod = resolveDefaultVerificationMethod(
      enabledMethods,
      user.otp_sent_via || twoFactorRecord.preferredLogin
    );

    setSelectedMethod(defaultMethod);

    if (isOtpMethod(defaultMethod) && user.otp_sent_via) {
      setOtpReady(true);
    }
    if (defaultMethod === "google_authenticator") {
      setOtpReady(true);
    }
  }, [
    enabledMethods,
    selectedMethod,
    twoFactorRecord.preferredLogin,
    user.otp_delivered,
    user.otp_sent_via,
  ]);

  useEffect(() => {
    if (!user.username?.trim() || !user.password) {
      history.replace("/auth");
    }
  }, [history, user.password, user.username]);

  // Drop stale "Token expirado" when a fresh OTP challenge UI mounts (timer > 0).
  // Only clears warning — keeps "Código enviado" info from login/resend.
  useEffect(() => {
    if (!otpReady || !isOtpMethod(selectedMethod)) return;
    clearOtpExpiredNotice();
    setVerificationDisable(false);
  }, [otpReady, selectedMethod, clearOtpExpiredNotice]);

  useEffect(() => {
    if (selectedMethod && !enabledMethods.includes(selectedMethod)) {
      setSelectedMethod(null);
      setOtpReady(false);
      setCode("");
    }
  }, [enabledMethods, selectedMethod]);

  const handleMethodSelect = useCallback(
    async (method: VerificationLoginMethod) => {
      if (
        method === selectedMethod &&
        otpReady &&
        isCodeMethod(method)
      ) {
        setShowMethodPicker(false);
        return;
      }

      setSelectedMethod(method);
      setCode("");
      setOtpReady(false);
      setShowMethodPicker(false);

      if (method === "google_authenticator") {
        setOtpReady(true);
        return;
      }

      if (!isOtpMethod(method)) {
        return;
      }

      if (!user.username?.trim() || !user.password) return;

      // Login already challenged this principal channel — reuse it; do not re-login
      // (avoids a second DispatchAsync). Explicit resend still goes through Countdown → resendCode.
      // Require otp_delivered so a failed SendGrid/SMTP attempt does not look like success.
      const alreadySent =
        user.otp_delivered === true
        && typeof user.otp_sent_via === "string"
        && user.otp_sent_via.toLowerCase() === method;
      if (alreadySent) {
        setOtpReady(true);
        return;
      }

      setIsSendingCode(true);
      try {
        const success = await sendVerificationCode({
          username: user.username,
          password: user.password,
          verification_method: method,
        });

        if (success) {
          setOtpReady(true);
        } else {
          enqueueSnackbar(
            "Não foi possível enviar o código. Tente novamente.",
            { variant: "error" }
          );
          setSelectedMethod(null);
        }
      } finally {
        setIsSendingCode(false);
      }
    },
    [
      enqueueSnackbar,
      otpReady,
      selectedMethod,
      sendVerificationCode,
      user.otp_sent_via,
      user.password,
      user.username,
    ]
  );

  const alternateMethods = useMemo(
    () => enabledMethods.filter((method) => method !== selectedMethod),
    [enabledMethods, selectedMethod]
  );

  useEffect(() => {
    if (!otpReady || !isCodeMethod(selectedMethod)) return;

    const focusOtp = () => otpInputRef.current?.focus();
    focusOtp();
    const timer = window.setTimeout(focusOtp, 100);
    return () => window.clearTimeout(timer);
  }, [otpReady, selectedMethod]);

  const handleValidatingCode = useCallback(() => {
    if (isLoading || verificationDisable || code.length !== 6) return;
    twoFactor({ code });
  }, [code, isLoading, twoFactor, verificationDisable]);

  const isFidoMockMode = process.env.REACT_APP_FIDO_MOCK === "true";

  const canUseFidoLogin = useMemo(
    () =>
      isFidoMockMode
        ? pilotPrefs.hasFidoKey
        : enabledMethods.includes("fido") &&
          (pilotPrefs.hasFidoKey || user.has_web_authn === true),
    [
      enabledMethods,
      isFidoMockMode,
      pilotPrefs.hasFidoKey,
      user.has_web_authn,
    ]
  );

  const handleFidoWebAuthn = useCallback(async () => {
    if (isFidoMockMode) {
      const record = readTwoFactorRecord(accountKey);

      if (record.credentials.length === 0) {
        throw new WebAuthnError(
          intl.get("auth.noUsbKeyForLogin"),
          "unknown"
        );
      }

      await authenticateSecurityKey({
        credentials: record.credentials.map((item) => ({
          id: item.id,
          transports: item.transports,
        })),
      });

      const loggedIn = await twoFactorFidoMock();
      if (!loggedIn) {
        throw new WebAuthnError(intl.get("auth.fidoLoginFailed"), "unknown");
      }
      return;
    }

    const document = normalizeLoginDocument(user.username) || accountKey;
    const result = await authenticateSecurityKey({ document });

    if (!result.auth?.access_token) {
      throw new WebAuthnError(intl.get("auth.fidoLoginFailed"), "unknown");
    }

    const loggedIn = await completeWebAuthnLogin(result.auth);
    if (!loggedIn) {
      throw new WebAuthnError(intl.get("auth.fidoLoginFailed"), "unknown");
    }
  }, [
    accountKey,
    completeWebAuthnLogin,
    isFidoMockMode,
    twoFactorFidoMock,
    user.username,
  ]);

  const fallbackOtpMethod = useMemo(
    () => resolveDefaultVerificationMethod(enabledMethods, user.otp_sent_via),
    [enabledMethods, user.otp_sent_via]
  );

  const otpMethodLabel = useMemo(() => {
    if (fallbackOtpMethod === "whatsapp") return intl.get("words.whatsapp");
    if (fallbackOtpMethod === "email") return intl.get("words.email");
    return intl.get("words.sms");
  }, [fallbackOtpMethod]);

  const subtitle = useMemo(() => {
    if (!selectedMethod) {
      return intl.get("auth.chooseVerificationMethod");
    }
    if (selectedMethod === "fido") {
      return intl.get("auth.subtitleFido");
    }
    if (selectedMethod === "google_authenticator") {
      return intl.get("auth.subtitleGoogle");
    }
    if (selectedMethod === "whatsapp") {
      return intl.get("auth.subtitleWhatsapp");
    }
    if (selectedMethod === "email") {
      return intl.get("auth.subtitleEmail");
    }
    if (isOtpMethod(selectedMethod)) {
      return intl.get("auth.subtitleSms", {
        phone: user.phone_number || intl.get("auth.registeredPhone"),
      });
    }
    return intl.get("auth.chooseVerificationMethod");
  }, [selectedMethod, user.phone_number]);

  const renderOtpForm = (options: { showResend: boolean } = { showResend: true }) => {
    const submitDisabled =
      isLoading ||
      code.length !== 6 ||
      (options.showResend && verificationDisable);

    return (
    <form
      className="login-verification-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (submitDisabled) return;
        handleValidatingCode();
      }}
    >
      <div className="login-verification-otp">
        <VerificationInput
          ref={otpInputRef}
          length={6}
          value={code}
          onChange={setCode}
          placeholder=""
          autoFocus
          validChars="0-9"
          classNames={{
            container: "login-verification-otp__container",
            character: "login-verification-otp__char",
            characterInactive: "login-verification-otp__char--inactive",
            characterSelected: "login-verification-otp__char--selected",
          }}
          inputProps={
            {
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                e.stopPropagation();
                if (submitDisabled) return;
                handleValidatingCode();
              },
            } as React.InputHTMLAttributes<HTMLInputElement>
          }
        />
      </div>

      {options.showResend ? (
        <div className="login-verification-countdown">
          <Countdown
            key={user.otp_delivered ? "otp-delivered" : "otp-undelivered"}
            expireTime={selectedMethod === "email" ? 60 : 45}
            unlockResendImmediately={!user.otp_delivered}
            resendCode={() => {
              // Clear expired banner as soon as the user renews (before API returns).
              clearOtpExpiredNotice();
              setVerificationDisable(false);
              if (isOtpMethod(selectedMethod)) {
                resendCode(selectedMethod);
              }
            }}
            isExpired={(isExpired: boolean) => {
              setVerificationDisable(isExpired);
            }}
            onExpired={
              user.otp_delivered ? showOtpExpiredNotice : undefined
            }
            resendLabel={
              selectedMethod === "whatsapp"
                ? intl.get("auth.resendWhatsapp")
                : selectedMethod === "email"
                ? intl.get("auth.resendEmail")
                : intl.get("auth.resendSms")
            }
          />
        </div>
      ) : (
        <p className="login-verification-method-hint">
          {intl.get("auth.googleCodeHint")}
        </p>
      )}

      <div className="login-verification-actions">
        <button
          type="button"
          className="login-verification-btn login-verification-btn--outline"
          onClick={() => {
            history.push("/auth");
          }}
        >
          {intl.get("words.back")}
        </button>
        <button
          type="submit"
          className="login-verification-btn login-submit-btn"
          disabled={submitDisabled}
        >
          {isLoading ? (
            <>
              <span className="login-submit-btn__spinner" />
              {intl.get("auth.verifying")}
            </>
          ) : (
            intl.get("auth.verify")
          )}
        </button>
      </div>
    </form>
    );
  };

  return (
    <div className="login-glass-card login-glass-card--verification">
      <div className="login-glass-card__header login-glass-card__header--center login-glass-card__header--verification">
        <h1 className="login-glass-card__title">{intl.get("auth.twoStepVerification")}</h1>
        <p className="login-glass-card__subtitle login-glass-card__subtitle--wide login-glass-card__subtitle--muted">
          {showMethodPicker
            ? intl.get("auth.chooseVerificationMethod")
            : subtitle}
        </p>

        {otpDeliveryNotice ? (
          <div
            className={`login-verification-alert${
              otpDeliveryNotice.variant === "warning"
                ? " login-verification-alert--warning"
                : ""
            }`}
            role="status"
            aria-live="polite"
          >
            {otpDeliveryNotice.variant === "warning" ? (
              <FiAlertTriangle
                className="login-verification-alert__icon"
                size={14}
                aria-hidden
              />
            ) : (
              <FiInfo
                className="login-verification-alert__icon"
                size={14}
                aria-hidden
              />
            )}
            <div className="login-verification-alert__content">
              <strong className="login-verification-alert__title">
                {otpDeliveryNotice.title}
              </strong>
              {otpDeliveryNotice.description ? (
                <p className="login-verification-alert__description">
                  {otpDeliveryNotice.description}
                </p>
              ) : null}
            </div>
            <button
              type="button"
              className="login-verification-alert__dismiss"
              onClick={clearOtpDeliveryNotice}
              aria-label={intl.get("words.close")}
            >
              <FiXCircle size={14} aria-hidden />
            </button>
          </div>
        ) : null}
      </div>

      <div className="login-verification-body">
        {showMethodPicker ? (
          <>
            <TwoFactorMethodPicker
              methods={enabledMethods}
              value={selectedMethod}
              onChange={handleMethodSelect}
            />
            <div className="login-verification-actions">
              <button
                type="button"
                className="login-verification-btn login-verification-btn--outline"
                onClick={() => setShowMethodPicker(false)}
              >
                {intl.get("words.back")}
              </button>
            </div>
          </>
        ) : (
          <>
            {!user.otp_delivered && user.dev_otp_code ? (
              <p
                className="login-verification-method-hint"
                style={{ fontWeight: 600 }}
              >
                {`[DEV] Código OTP: ${user.dev_otp_code} (envio por SMS/WhatsApp falhou — use este código)`}
              </p>
            ) : null}

            {isOtpMethod(selectedMethod) && otpReady ? (
              renderOtpForm({ showResend: true })
            ) : selectedMethod === "google_authenticator" && otpReady ? (
              renderOtpForm({ showResend: false })
            ) : isOtpMethod(selectedMethod) && isSendingCode ? (
              <div className="login-verification-form login-verification-form--loading">
                <span className="login-submit-btn__spinner" aria-hidden />
                <p className="login-verification-method-hint">
                  {intl.get("auth.signingIn")}
                </p>
              </div>
            ) : selectedMethod === "fido" ? (
              <div className="login-verification-form">
                {!canUseFidoLogin ? (
                  <div className="login-verification-fido-hint">
                    <p>
                      {intl.get("auth.noUsbKeyHint", { method: otpMethodLabel })}
                    </p>
                    <button
                      type="button"
                      className="login-verification-btn login-submit-btn mt-4"
                      onClick={() => handleMethodSelect(fallbackOtpMethod)}
                    >
                      <FiSmartphone className="h-4 w-4" aria-hidden />
                      {intl.get("auth.signInWithMethod", { method: otpMethodLabel })}
                    </button>
                  </div>
                ) : (
                  <FidoTouchPanel
                    onWebAuthn={handleFidoWebAuthn}
                    disabled={isLoading}
                    idleMessage={intl.get("auth.fidoInsertTouch")}
                    buttonLabel={intl.get("auth.signInWithUsbKey")}
                    onError={(message) => {
                      enqueueSnackbar(message, { variant: "error" });
                    }}
                  />
                )}

                <div className="login-verification-actions login-verification-actions--fido">
                  <button
                    type="button"
                    className="login-verification-btn login-verification-btn--outline"
                    onClick={() => {
                      history.push("/auth");
                    }}
                  >
                    {intl.get("words.back")}
                  </button>
                </div>
              </div>
            ) : (
              <div className="login-verification-actions">
                <button
                  type="button"
                  className="login-verification-btn login-verification-btn--outline"
                  onClick={() => {
                    history.push("/auth");
                  }}
                >
                  {intl.get("words.back")}
                </button>
              </div>
            )}

            {alternateMethods.length > 0 && selectedMethod ? (
              <div className="mt-1 flex justify-center">
                <button
                  type="button"
                  className="login-verification-btn login-verification-btn--outline"
                  onClick={() => setShowMethodPicker(true)}
                  disabled={isSendingCode || isLoading}
                >
                  {intl.get("auth.useOtherMethod")}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default VerificationCodeLogin;
